<script setup lang="ts">
import { blogCategoryLabel, blogLocaleFor, blogPostPath, formatBlogDate } from '~/constants/blog'
import type { BlogPost, BlogPostListItem } from '~/types'

/**
 * Blog card from the template: bordered rounded box, thumbnail that zooms on
 * hover, dark category pill next to the date, clamped title and excerpt and a
 * sliding "Read more" arrow.
 *
 * `horizontal` is the wide card of the listing pages; `vertical` is the
 * compact variant for the three-column grid on the homepage. Both keep real
 * hrefs on the title, image and category so the crawl graph does not depend
 * on the click handler.
 */
interface Props {
  // Accepts listing items too — those omit `content`, which this card never reads.
  post: BlogPost | BlogPostListItem
  maxTags?: number
  layout?: 'vertical' | 'horizontal'
}

const props = withDefaults(defineProps<Props>(), {
  maxTags: 3,
  layout: 'vertical',
})
const router = useRouter()
const { t } = useI18n()
const { scrollToTopInstant } = useScrollToTop()

// Category pages exist for the English blog only.
const categoryLabel = computed(() => (blogLocaleFor(props.post.locale) === 'en' ? blogCategoryLabel(props.post.category) : null))
const postUrl = computed(() => blogPostPath(props.post))
const compact = computed(() => props.layout === 'vertical')

const navigateToPost = () => {
  scrollToTopInstant()
  router.push(postUrl.value)
}

const formatDate = (dateString: string) => formatBlogDate(dateString, props.post.locale)
</script>

<template>
  <article
    class="group flex h-full cursor-pointer flex-col rounded-[1.25rem] border border-line bg-white transition-all duration-300 hover:shadow-lift dark:border-gray-700 dark:bg-gray-900"
    :class="compact ? 'p-5' : 'p-5 xl:p-[1.875rem]'"
    @click="navigateToPost"
  >
    <!-- Thumbnail -->
    <LocaleLink
      v-if="post.image_url"
      :to="postUrl"
      class="block overflow-hidden bg-surface dark:bg-gray-800"
      :class="compact ? 'rounded-[0.625rem]' : 'rounded-[0.9375rem]'"
      tabindex="-1"
      aria-hidden="true"
    >
      <NuxtImg
        :src="post.image_url"
        :alt="t('blog.featuredImageAlt', { title: post.title })"
        :width="compact ? 480 : 790"
        :height="compact ? 300 : 500"
        :sizes="compact ? 'sm:100vw md:50vw lg:33vw' : 'sm:100vw lg:60vw'"
        format="webp"
        quality="80"
        loading="lazy"
        class="aspect-[79/50] size-full object-cover transition-transform duration-300 group-hover:scale-110"
      />
    </LocaleLink>

    <!-- Meta: category pill + date + read time -->
    <div
      class="flex flex-wrap items-center gap-x-4 gap-y-3 text-base"
      :class="compact ? 'my-5' : 'my-[1.875rem]'"
    >
      <!--
        The guide's pill is `.btn` with overrides; those need `!` because
        `.sofax .btn` outranks a bare utility. `btn-dark` supplies the ink
        background and its dark-mode inverse in one go.
      -->
      <LocaleLink
        v-if="post.category && categoryLabel"
        :to="`/blog/category/${post.category}`"
        class="btn btn-dark !min-w-0 !rounded-[5px] !px-4 !py-2 !text-base"
        :class="{ '!text-sm': compact }"
      >
        <span>{{ categoryLabel }}</span>
        <span aria-hidden="true">{{ categoryLabel }}</span>
      </LocaleLink>

      <time :datetime="post.published_at || post.created_at" class="text-body dark:text-gray-300">
        {{ formatDate(post.published_at || post.created_at) }}
      </time>

      <span class="inline-flex items-center gap-1.5 text-muted dark:text-gray-400">
        <i class="bi bi-clock" aria-hidden="true" />
        {{ post.read_time_minutes }} {{ t('blog.readTime') }}
      </span>
    </div>

    <!-- Content -->
    <div class="flex grow flex-col">
      <h3 class="line-clamp-2" :class="{ '!text-xl md:!text-2xl': compact }">
        <LocaleLink :to="postUrl" class="hover:text-accent dark:hover:text-accent-soft">
          {{ post.title }}
        </LocaleLink>
      </h3>

      <p v-if="post.description" class="mt-4 line-clamp-2" :class="{ 'text-base': compact }">
        {{ post.description }}
      </p>

      <!-- Tags: no tag pages exist, so these stay plain chips -->
      <div v-if="post.tags.length > 0" class="mt-5 flex flex-wrap gap-2">
        <span
          v-for="tag in post.tags.slice(0, maxTags)"
          :key="tag"
          class="chip !px-4 !py-1.5 !text-sm"
        >
          {{ tag }}
        </span>
        <span v-if="post.tags.length > maxTags" class="chip !px-4 !py-1.5 !text-sm">
          +{{ post.tags.length - maxTags }}
        </span>
      </div>

      <div class="mt-auto pt-6">
        <SofaxArrowLink :to="postUrl" :label="t('blog.readMore')" :sr-suffix="cleanTitle(post.title)" />
      </div>
    </div>
  </article>
</template>
